import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { money, maskCPF } from "@/lib/utils-app";
import { toast } from "sonner";
import { CreditCard, QrCode, Store, Banknote, ShieldCheck, Fingerprint } from "lucide-react";

export default function CheckoutPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [os, setOs] = useState(null);
  const [method, setMethod] = useState("online");
  const [pickup, setPickup] = useState("Pix");
  const [loading, setLoading] = useState(false);
  const [cpf, setCpf] = useState("");
  const isFreight = new URLSearchParams(window.location.search).get("freight") === "1";

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/track/${code}`); setOs(data); } catch { toast.error("OS não encontrada"); }
  })(); }, [code]);

  const submit = async () => {
    const digits = (cpf || "").replace(/\D/g, "");
    if (digits.length < 11) { toast.error("Digite seu CPF completo para confirmar"); return; }
    setLoading(true);
    try {
      if (isFreight) {
        const { data } = await api.post("/payments/checkout?freight=1", {
          os_code: code,
          origin_url: window.location.origin,
          customer_doc: digits,
          kind: "freight",
        });
        window.location.href = data.checkout_url;
      } else if (method === "online") {
        const { data } = await api.post("/payments/checkout", {
          os_code: code,
          origin_url: window.location.origin,
          customer_doc: digits,
        });
        window.location.href = data.checkout_url;
      } else {
        await api.post("/payments/pay-on-pickup", { os_code: code, method: pickup, customer_doc: digits });
        toast.success("Registrado! Aguardaremos você na retirada.");
        nav(`/rastrear/${code}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  if (!os) return <div className="min-h-screen bg-neutral-50"><Header /><div className="p-12 text-center text-neutral-500">Carregando…</div></div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Finalizar</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Pagamento</h1>

        <div className="mt-10 grid lg:grid-cols-5 gap-6">
          {/* Summary */}
          <div className="lg:col-span-2 bg-neutral-900 text-white rounded-2xl p-6 h-fit">
            <div className="text-xs uppercase tracking-widest text-neutral-400">Resumo · {os.os_code}</div>
            <div className="mt-4 text-sm text-neutral-300">{os.device_brand} {os.device_model}</div>
            {isFreight ? (
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-neutral-300">Taxa de envio</span><span className="font-mono">{money(os.freight_amount || 0)}</span></div>
                <div className="text-[11px] text-neutral-400 mt-2">O pagamento libera o envio do aparelho para o endereço cadastrado.</div>
              </div>
            ) : (
              <div className="mt-6 space-y-2 text-sm">
                {os.parts?.map((p, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-neutral-300">{p.qty}x {p.name}</span>
                    <span className="font-mono">{money(p.unit_price * p.qty)}</span>
                  </div>
                ))}
                {os.labor_cost > 0 && (
                  <div className="flex justify-between"><span className="text-neutral-300">Mão de obra</span><span className="font-mono">{money(os.labor_cost)}</span></div>
                )}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-neutral-800 flex justify-between items-baseline">
              <div className="text-xs uppercase tracking-widest text-neutral-400">Total</div>
              <div className="font-mono text-3xl font-bold" data-testid="checkout-total">{money(isFreight ? (os.freight_amount || 0) : os.total)}</div>
            </div>
          </div>

          {/* Options */}
          <div className="lg:col-span-3 bg-white border border-neutral-200 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-4">
              {isFreight ? "Pagamento do frete" : "Forma de pagamento"}
            </div>

            {isFreight ? (
              <div className="p-4 border border-neutral-200 rounded-xl bg-neutral-50">
                <div className="font-semibold flex items-center gap-2"><CreditCard size={16}/> Pagar frete online</div>
                <div className="text-sm text-neutral-600 mt-1">Pix ou Cartão de crédito processados pelo <b>Mercado Pago</b>. Confirmação automática.</div>
                <div className="mt-3 flex gap-2 items-start text-[11px] bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-emerald-900">
                  <ShieldCheck size={13} className="mt-0.5 shrink-0"/>
                  <div>Você será redirecionado ao ambiente seguro do Mercado Pago para concluir o pagamento.</div>
                </div>
              </div>
            ) : (
            <RadioGroup value={method} onValueChange={setMethod} className="space-y-3">
              <label htmlFor="pm-online" className="flex gap-3 items-start p-4 border border-neutral-200 rounded-xl cursor-pointer hover:border-neutral-900 has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-50">
                <RadioGroupItem value="online" id="pm-online" data-testid="pm-online" />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2"><CreditCard size={16}/> Pagar online <span className="ml-1 text-[10px] font-bold uppercase tracking-widest bg-neutral-900 text-white px-2 py-0.5 rounded">Recomendado</span></div>
                  <div className="text-sm text-neutral-600 mt-1">Pix ou Cartão de crédito processados pelo <b>Mercado Pago</b>. Confirmação automática, parcele em até 12x.</div>

                  {method === "online" && (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="p-3 rounded-lg border border-neutral-200 flex items-center gap-2 text-xs"><QrCode size={16} className="text-neutral-700"/> <b>Pix</b> · confirmação em segundos</div>
                      <div className="p-3 rounded-lg border border-neutral-200 flex items-center gap-2 text-xs"><CreditCard size={16} className="text-neutral-700"/> <b>Cartão</b> · até 12x</div>
                    </div>
                  )}

                  {method === "online" && (
                    <div className="mt-3 flex gap-2 items-start text-[11px] bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-emerald-900" data-testid="mp-secure-notice">
                      <ShieldCheck size={13} className="mt-0.5 shrink-0"/>
                      <div>Você será redirecionado ao ambiente seguro do Mercado Pago para concluir o pagamento.</div>
                    </div>
                  )}
                </div>
              </label>
              <label htmlFor="pm-pickup" className="flex gap-3 items-start p-4 border border-neutral-200 rounded-xl cursor-pointer hover:border-neutral-900 has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-50">
                <RadioGroupItem value="pickup" id="pm-pickup" data-testid="pm-pickup" />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2"><Store size={16}/> Pagar na retirada</div>
                  <div className="text-sm text-neutral-600 mt-1">Escolha a forma pretendida. Pague ao retirar o aparelho.</div>

                  {method === "pickup" && (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {[
                        { v: "Pix", i: QrCode }, { v: "Dinheiro", i: Banknote },
                        { v: "Cartão de Débito", i: CreditCard }, { v: "Cartão de Crédito", i: CreditCard }
                      ].map(({ v, i: Icn }) => (
                        <button type="button" key={v} onClick={(e) => { e.preventDefault(); setPickup(v); }}
                          className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${pickup === v ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-400"}`}
                          data-testid={`pickup-${v.toLowerCase().replace(/\s/g, "-")}`}
                        ><Icn size={14}/> {v}</button>
                      ))}
                    </div>
                  )}
                </div>
              </label>
            </RadioGroup>
            )}

            <div className="mt-6 p-4 bg-neutral-50 border border-neutral-200 rounded-xl">
              <Label htmlFor="checkout-cpf" className="text-xs uppercase tracking-widest font-bold text-neutral-700 flex items-center gap-1.5">
                <Fingerprint size={13}/> Confirme seu CPF
              </Label>
              <p className="text-[11px] text-neutral-500 mt-1">
                Última verificação de segurança — precisa bater com o CPF cadastrado na OS. Não fica salvo, é só para autorizar este pagamento.
              </p>
              <Input
                id="checkout-cpf"
                data-testid="checkout-cpf"
                value={cpf}
                onChange={(e) => setCpf(maskCPF(e.target.value))}
                placeholder="000.000.000-00"
                className="mt-2 h-11 font-mono text-base"
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <Button onClick={submit} disabled={loading} className="w-full h-14 mt-6 rounded-md bg-neutral-900 hover:bg-neutral-800 text-base" data-testid="checkout-submit">
              {loading
                ? "Processando…"
                : isFreight
                  ? `Pagar frete ${money(os.freight_amount || 0)} com Mercado Pago`
                  : method === "online"
                    ? `Pagar ${money(os.total)} com Mercado Pago`
                    : "Registrar pagamento na retirada"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
